<!--
=========================================================================================
    Description: Unify Communication Center
    This module can bring forth super convenient to user to manage their all communication channels here.
==========================================================================================
-->

<template>
  <div>
    <div
      class="component-block
        border border-solid d-theme-border-grey-light rounded relative overflow-hidden">

      <!-- COMPONENT HEAD (TITLE BAR) -->
      <div class="flex flex-inline justify-between bg-primary-gradient">
        <!-- TITLE -->
        <h3 class="_title px-5 py-4 text-white">Communication</h3>

        <!-- COMMON LIST SELECTOR PANEL -->
        <div class="m-2 p-2">
          <vs-button
            class="list-type-button mx-1" size="small"
            :color="$data.$local.activeListType === 'recent' ? 'white' : 'transparent'"
            :class="{ '_active text-primary': $data.$local.activeListType === 'recent' }"
            @click="changeListType('recent')">{{ $t('general.recent') }}</vs-button>
          <vs-button
            class="list-type-button mx-1" size="small"
            :color="$data.$local.activeListType === 'unread' ? 'white' : 'transparent'"
            :class="{ '_active text-primary': $data.$local.activeListType === 'unread' }"
            @click="changeListType('unread')">{{ $t('general.unread') }}</vs-button>
          <vs-button
            class="list-type-button mx-1" size="small"
            :color="$data.$local.activeListType === 'all' ? 'white' : 'transparent'"
            :class="{ '_active text-primary': $data.$local.activeListType === 'all' }"
            @click="changeListType('all')">{{ $t('general.all') }}</vs-button>
        </div>
      </div>

      <!-- COMPONENT BODY -->
      <div id="communication-comp">

        <!-- SIDEBAR for select channel of communication -->
        <Sidebar />

        <!-- MESSAGE LIST for showing list of messages from each communication channel -->
        <div
          class="no-scroll-content border border-solid d-theme-border-grey-light border-r-0 border-t-0 border-b-0 overflow-auto"
          :class="{'sidebar-spacer': $data.$local.clickNotClose}"
          style="max-height: 75vh"
          >
          <component :is="scrollbarTag">
            <TopicListControl />
          </component>
        </div>

        <!-- MESSAGE CONTENT PANEL -->
        <vs-sidebar
          parent="#communication-comp"
          click-not-close
          :hidden-background="true"
          class="full-vs-sidebar email-view-sidebar items-no-padding"
          v-model="$data.$local.isMessageContentActive"
          position-right>

          <div class="mail-sidebar-content px-0 sm:pb-6 h-full" v-if="currentTopic">

            <div
              class="flex flex-wrap items-center email-header justify-between md:px-8 px-6 sm:pb-2 sm: pt-6 d-theme-dark-bg">

              <div class="flex mb-4">
                <div class="flex items-center">
                  <feather-icon
                    :icon="$vs.rtl ? 'ArrowRightIcon' : 'ArrowLeftIcon'"
                    svg-classes="w-6 h-6"
                    class="cursor-pointer mr-4"
                    @click="closeMessageContentView"></feather-icon>
                  <!-- <h4 v-if="currentMessage.subject">{{ currentMessage.subject }}</h4>
                  <h4 v-else>(no subject)</h4> -->
                </div>
              </div>

              <div class="ml-10 mb-4 mt-1">
                <div class="email__actions--single flex items-baseline">

                  <feather-icon
                    :icon="$vs.rtl ? 'ChevronsRightIcon' : 'ChevronsLeftIcon'"
                    svg-classes="h-6 w-6"
                    class="cursor-pointer ml-4 hidden sm:inline-flex"
                    @click="previousMessage" />
                  <feather-icon
                    :icon="$vs.rtl ? 'ChevronsLeftIcon' : 'ChevronsRightIcon'"
                    svg-classes="h-6 w-6"
                    class="cursor-pointer ml-4 hidden sm:inline-flex"
                    @click="nextMessage" />
                </div>
              </div>
            </div>

            <component :is="scrollbarTag"
              class="scroll-area-mail-content md:px-6 md:pb-6 pt-2 px-4 pb-4"
              :key="$vs.rtl">

              <!-- LABEL ROW -->
              <div class="vx-row">
                <div class="vx-col w-full">
                  <div class="email__labels--single flex ml-10 items-center mt-2">
                    <transition-group name="list" tag="div" class="flex">
                      <!-- <div v-for="label in currentMail.labels" :key="label" class="open-mail-label flex items-center mr-4">
                        <div class="h-3 w-3 rounded-full mr-1" :class="'bg-' + labelColor(label)"></div>
                        <span class="text-sm">{{ label | capitalize }}</span>
                      </div> -->
                    </transition-group>
                  </div>
                </div>
              </div>

              <!-- LATEST MESSAGE -->
              <div class="vx-row">
                <div class="vx-col w-full">
                  <TopicPageController :data="$data.$local.currentTopic" :type="$data.$local.currentViewingMessageType" />
                </div>
              </div>

            </component>
          </div>
        </vs-sidebar>
      </div>
    </div>
  </div>
</template>

<script>
import { state, loadTopicList } from "./data";
import CommunicationStoreModule from "@/developing/store/modules/communication";

export default {
  components: {
    VuePerfectScrollbar:() => import ("vue-perfect-scrollbar"),
    //
    Sidebar:() => import("./subcomponents/Sidebar"),
    //
    TopicListControl:() => import("./subcomponents/TopicListControl"),
    //
    TopicPageController:() => import("./subcomponents/TopicPageController"),
    //
    MessageHeader: () => {
      const type = state.local.currentViewingMessageType;
      if (type === "pm") { return import("./subcomponents/PM/MessageHeader") }
      if (type === "webboard") { return import("./subcomponents/Webboard/MessageHeader") }
      if (type === "chat") { return import("./subcomponents/Chat/MessageHeader") }
      return null;
    }
  },
  data() {
    return {
      $local: state.local,
    }
  },
  //
  async beforeCreate() {
    // console.log("CHECK", this.$store.state);

    if (!this.$store.state.Communication) {
      // const CommunicationStoreModule = async () => await import("@/developing/store/modules/communication");

      // console.log(CommunicationStoreModule);
      this.$store.registerModule("Communication", CommunicationStoreModule);
    }
  },
  //
  async created() {
    await loadTopicList();
  },
  //
  async beforeMount() {
    await this.getLatestMessagesAllChannels();
  },
  //
  async destroyed() {
    this.$store.unregisterModule("Communication");
  },
  computed: {
    //
    scrollbarTag() {
      return this.$store.getters['scrollbarTag'];
    },
    //
    currentTopic() {
      return this.$data.$local.currentTopic;
      // const messages = this.$data.$local.messagePool[this.$data.$local.currentViewingMessageType];

      // // console.log("currentMessage", this.$data.$local.currentViewingMessageType, this.$data.$local.messagePool[this.$data.$local.currentViewingMessageType]);
      // if (currentMessageId && messages) {
      //   return messages.find(message => message.id === currentMessageId);
      // }

      // return null;
    },
  },
  methods: {
    //
    closeMessageContentView() {
      this.$data.$local.isMessageContentActive = false;
    },
    //
    previousMessage() {
      if (this.$data.$local.currentViewingMessageCursor > 0
      ) {
        this.$data.$local.currentViewingMessageCursor -= 1;
      }
    },
    //
    nextMessage() {
      if (this.$data.$local.currentViewingMessageCursor < this.$data.$local.currentViewingMessages.length - 1) {
        this.$data.$local.currentViewingMessageCursor += 1;
      }
    },
    //
    bindMessageTopicsFromPool() {

    },
    //
    async getLatestMessagesAllChannels() {

      // const { status, data } = await this.$store.dispatch("Communication/getLatestMessageAllChannels",
      //   {}
      // );

      // if (status.success) {
      //   console.log("CHECK", data);
      //   this.setMessagesToChannels(data);
      // }
    },
    //
    setMessagesToChannels(data) {
      this.setMessageTopicsForChannel("pm", data.pm);
      this.setMessageTopicsForChannel("webboard", data.webboard);
      this.setMessageTopicsForChannel("chat", data.chat);
    },
    //
    setMessageTopicsForChannel(channel, topics) {
      // console.log("setMessageTopicsForChannel", channel, topics);

      for (let topic of topics) {
        const pool = this.$data.$local.messagePool[channel];
        if (pool && pool.findIndex(m => m.id === topic.id) < 0) {
          this.$data.$local.messagePool[channel].unshift(this.messageAdapter(channel, topic))
        }
      }
    },
    //
    messageAdapter(channel, message) {
      if (channel === "pm") {
        const { id, topic, sendDate, readDate, fromUser, fromUserCode } = message;
        const sender_name =
          `${fromUser ? fromUser.nameLocal : "" }
            ${fromUser ? fromUser.surnameLocal : "" }`;
        const sender_code =
          `${fromUserCode ? fromUserCode : ""}`
        return {
          ...message,
          id,
          type: "pm",
          subject: topic,
          senderUUID: fromUser ? fromUser.photo : null,
          sender_name,
          sender_code,
          unread: readDate ? false : true,
          time: sendDate,
          isStarred: true,
          content: message.message
        }
      }
      if (channel === "chat") {
        const { id, topic, sendDate, readDate, fromUser, fromUserCode } = message;
        const sender_name = `${fromUser ? fromUser.nameLocal : "" } ${fromUser ? fromUser.surnameLocal : "" } (${fromUserCode})`;
        return {
          ...message,
          id,
          type: "chat",
          subject: topic,
          senderUUID: fromUser ? fromUser.photo : null,
          sender_name,
          unread: readDate ? false : true,
          time: sendDate,
          isStarred: true,
          content: message.message
        }
      }
      if (channel === "webboard") {
        const { id, topic, sendDate, readDate, /* teacher, teacherCode, */ student, studentCode } = message;
        const sender_name = `${student ? student.firstNameLocal : "" } ${student ? student.lastNameLocal : "" } (${studentCode})`;
        return {
          ...message,
          id,
          type: "webboard",
          subject: topic,
          senderUUID: "",
          sender_name,
          unread: readDate ? false : true,
          time: sendDate,
          isStarred: true,
          content: message.message
        }
      }
    },
    //
    changeListType(type) {
      this.$data.$local.activeListType = type;
    },
    //
    getCurrentMessage() {
      // const currentMessageId = state.local.currentViewingMessages[state.local.currentViewingMessageCursor];
      // const message = await Datastore.Message.load("message", state.local.currentViewingMessageType, currentMessageId);
      // if (message) {
      //   return message
      // }
      return null
    }
  },
}
</script>

<style lang="scss"> @import "./communication.scss"; </style>
